import { useMemo } from 'react';
import { useInfiniteHits } from 'react-instantsearch';
import styled from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import useInfiniteScroll from '@sorare/core/src/hooks/useInfiniteScroll';
import { CardHit } from '@sorare/core/src/lib/algolia';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import SearchBox from 'search/SearchBox';

import { Grid } from '../Grid';
import { Header } from '../Header';
import { PositionFilter } from '../PositionFilter';
import { TeamFilters } from '../TeamFilters';

const Wrapper = styled(Vertical).attrs({ gap: 2 })`
  padding: var(--triple-unit);
  position: relative;
`;
const Sticky = styled(Vertical).attrs({ gap: 2 })`
  background-color: var(--c-neutral-100);
  position: sticky;
  top: 56px;
  z-index: 1;
  padding: var(--double-unit) 0;
  margin-top: calc(-1 * var(--double-unit));
  @media ${tabletAndAbove} {
    top: calc(8 * var(--unit));
    padding: var(--double-unit) 0;
  }
`;

type Props = {
  onPrimaryBuySuccess: () => void;
  gameSlug: string;
  onInfoClick?: (cardSlug: string, playerSlug: string) => void;
  isNationalTeam: boolean;
  startingLineup?: string[];
};
export const Content = ({
  onPrimaryBuySuccess,
  onInfoClick,
  gameSlug,
  isNationalTeam,
  startingLineup,
}: Props) => {
  const { items, showMore, isLastPage } = useInfiniteHits<CardHit>();

  const { InfiniteScrollLoader } = useInfiniteScroll(
    showMore,
    !isLastPage,
    false
  );

  const playersNamesForPlaceholder = items.slice(0, 3).reduce((acc, hit, i) => {
    const {
      player: { display_name },
    } = hit;
    return i === 0 ? `${display_name}...` : `${display_name}, ${acc}`;
  }, '');

  const playerSlugs = useMemo(() => {
    const slugs = items?.map(hit => hit.player.slug) || [];
    if (startingLineup?.length) {
      return [
        ...slugs.filter(slug => startingLineup.includes(slug)),
        ...slugs.filter(slug => !startingLineup.includes(slug)),
      ];
    }
    return slugs;
  }, [items, startingLineup]);

  return (
    <Wrapper>
      <Header />
      <Sticky>
        <SearchBox placeholder={playersNamesForPlaceholder} withIcon />
        <Horizontal gap={2}>
          <TeamFilters isNationalTeam={isNationalTeam} />
          <PositionFilter />
        </Horizontal>
      </Sticky>
      <Grid
        playerSlugs={playerSlugs}
        onPrimaryBuySuccess={onPrimaryBuySuccess}
        gameSlug={gameSlug}
        onInfoClick={onInfoClick}
        startingLineup={startingLineup}
      />
      <InfiniteScrollLoader />
    </Wrapper>
  );
};
